import { setRiotBarAuthState } from "@/utils/riotbar";
import { useI18n } from "@/utils/rioti18n";
import { safeLocalStorage } from "@/utils/safeStorages";
import { CallbackMode, FetchOptions, RSO, RSOLoginApi, SessionDetails } from "@riot-private/rso-login";
import React from "react";
import logger from "../utils/logger";
import siteConfig, { gameSpecificConfig } from "../variables";

/**
 * Helper function to parse tokens taking into consideration utf-8 characters.
 *
 * Given a token, we split it into segment and using the middle segment we decode and parse the resulting JSON.
 * Without using both decodeURIComponent & escape here we noticed issues with parsing Korean names.
 *
 * @param token
 * @returns parsed token object
 */
export const parseIdToken = (token: string) => {
  const base64Payload = toBase64(token.split(".")[1]);
  return JSON.parse(decodeURIComponent(escape(atob(base64Payload))));
};

const toBase64 = (base64url: string): string => {
  const base64 = base64url.replace(/-/g, "+").replace(/_/g, "/");
  return base64 + "=".repeat((4 - (base64.length % 4)) % 4);
};

export type SessionState = {
  idToken?: string;
  accessToken?: string;
};

export type AuthState = {
  rsoReady: boolean;
  user?: {
    game_name: string | null;
    tag_line: string;
  };
  country?: string;
  session?: SessionState;
  login: CallableFunction;
  logout: CallableFunction;
  clearSession: CallableFunction;
  hookLoginFunctions: CallableFunction;
  rsoSession?: SessionDetails;
};

type SessionDetailsWithGame = SessionDetails & { gameId?: string };

const EMPTY_AUTH_STATE: AuthState = {
  rsoReady: false,
  user: undefined,
  session: undefined,
  login: () => void 0,
  logout: () => void 0,
  clearSession: () => void 0,
  hookLoginFunctions: () => void 0,
  rsoSession: undefined,
};

const RSOContext = React.createContext<AuthState>(EMPTY_AUTH_STATE);

export const useRSOAuth = () => React.useContext(RSOContext);

/**
 *
 * rsoAuth is a helper to deal with setting and retriving the idToken from rso-login.
 *
 * The hook tries to retrieve any existing token from cookies / storage,
 *
 *  */
export const RSOProvider = ({ children, game }: { game?: string; children?: React.ReactNode }) => {
  const [authState, setAuthState] = React.useState<AuthState>(EMPTY_AUTH_STATE);
  const [rsoReady, setRsoReady] = React.useState(false);
  const [forceClearSession, setForceClearSession] = React.useState(false);
  const [rsoInstance, setRsoInstance] = React.useState<RSOLoginApi>();
  const [rsoSession, setRsoSession] = React.useState<SessionDetailsWithGame>();
  const { language: locale } = useI18n();

  // Lets load the right config, depending on the game
  // In development if you hit wildrift products, you need to use wr-int, whereas league, and valorant can use staging.
  // In production, this will use production config, so no changes there.
  let rsoConfig = siteConfig;
  let currentGame: string | undefined;
  if (siteConfig.BUILD_ENV !== "production") {
    if (game) {
      safeLocalStorage.setItem(siteConfig.KEY_GAME_ID, game);
      currentGame = game;
    } else {
      currentGame = safeLocalStorage.getItem(siteConfig.KEY_GAME_ID) ?? undefined;
    }
    if (currentGame && currentGame in gameSpecificConfig) {
      rsoConfig = gameSpecificConfig[currentGame];
    }
  }

  // We want to generate a singleton of the rso instance.
  React.useEffect(() => {
    RSO.init({
      clientId: rsoConfig.RSO_CLIENT_ID,
      rsoHost: rsoConfig.RSO_HOST,
      callbackMode: CallbackMode.CODE_QUERY,
      fetch: FetchOptions.TOKENS,
      onReady(err, rso) {
        setRsoInstance(rso);
        if (err) {
          logger("RSO.init error", err, "error");
          return;
        }
        // We can only get the session in new login in /redirect page
        let newSession: SessionDetailsWithGame = rso.getSession();
        if (newSession.idToken && newSession.accessToken) {
          if (currentGame) {
            newSession = { ...newSession, gameId: currentGame };
          }
          setRsoSession(newSession);
        }
        setRsoReady(true);
      },
    });
  }, []);

  /**
   * Creates the login function to replace the default Riotbar login function
   */
  const login = React.useMemo(() => {
    if (typeof window === "undefined") {
      return async () => void 0;
    }
    const { origin } = new URL(window.location?.href);
    const redirectUri = `${origin}/redirect/`;
    const scope = ["account openid email"];

    // Convert the locale used by contentstack to an appropriate locale used in RSO.
    const currentLocale = locale.split("-")[0] ?? null;

    return async () => {
      if (!rsoInstance) {
        const error = new Error("rsoInstance not available");
        logger("rsoInstance not available", error);
        throw error;
      }
      const { ui_locales_supported } = rsoInstance.getOpenIdConfig();
      const locale = ui_locales_supported.find((l) => l.split("-")[0] == currentLocale);
      rsoInstance.signIn({ scope, redirectUri, callbackMode: CallbackMode.CODE_QUERY, locale });
    };
  }, [rsoInstance]);

  /**
   * Creates a clear session function
   *
   * This should remove the auth state from the riotbar, as well as clearing any storage
   */
  const clearSession = React.useMemo(() => {
    return (updateAuthState = false) => {
      safeLocalStorage.removeItem(siteConfig.KEY_RSO_SESSION);
      if (updateAuthState) {
        setRiotBarAuthState(false, "", "");
      }
      /*
       * Page loaded, player is logged-in state
       * FE will hit `product_quote` endpoint
       * If API 401 error we are going to clear the session
       * We have to reset the session object to undefined as well
       * So that `useProductInfo` will hit `/product` endpoint
       */
      setForceClearSession(true);
      setRsoSession(undefined);
    };
  }, []);

  /**
   * Creates the logout function to replace the default Riotbar logout function
   */
  const logout = React.useMemo(() => {
    if (typeof window === "undefined") {
      return async () => void 0;
    }
    const { origin } = new URL(window.location.href);
    const redirectUri = `${origin}/redirect/`;
    const signOutOptions = { redirectUri, locale };

    return () => {
      if (!rsoInstance) {
        const error = new Error("rsoInstance not available");
        logger("rsoInstance not available", error);
        throw error;
      }
      clearSession();
      rsoInstance.signOut(signOutOptions);
    };
  }, [rsoInstance, clearSession]);

  /**
   * Hooks into the riotbar auth functions to use the library instead
   */
  const hookLoginFunctions = React.useMemo(() => {
    if (typeof window === "undefined") {
      return () => void 0;
    }
    return () => {
      window.RiotBar.account.login = login;
      window.RiotBar.account.logout = logout;
    };
  }, [login, logout]);

  // Get any previously saved session
  const savedSession = React.useMemo(() => {
    if (forceClearSession) {
      setForceClearSession(false);
    }

    if (!rsoReady) {
      return;
    }

    const currentSession: any = safeLocalStorage.getItem(siteConfig.KEY_RSO_SESSION);
    if (!currentSession) {
      return null;
    }
    try {
      // Check if the token is expired
      const parsedSession = JSON.parse(currentSession);
      // Check if the token game matches the current game (only on non production)
      if (siteConfig.BUILD_ENV !== "production" && parsedSession.gameId !== currentGame) {
        clearSession();
        return null;
      }
      const { exp } = JSON.parse(atob(toBase64(parsedSession.accessToken.split(".")[1])));
      // https://stackoverflow.com/a/51293316/1428241
      const isTesting = process.env.NODE_ENV === "test";
      if (!isTesting && Date.now() >= exp * 1000) {
        // Removes session from localstorage
        clearSession();
        return null;
      } else {
        // Player's token is not expired
        return parsedSession;
      }
    } catch (e) {
      logger("Unable to parse the saved session.", e as Error, "error");
      return null;
    }
  }, [rsoReady, forceClearSession]);

  // Get the current session
  const session = React.useMemo(() => {
    if (typeof window === "undefined") {
      return savedSession;
    }
    // Player is logging-in first time && Works only on /redirect page
    if (!savedSession && rsoSession) {
      // Extract last pathname: https://stackoverflow.com/a/51795122/1428241
      const urlSegments = new URL(window.location.href).pathname.split("/");
      const urlLast = urlSegments.pop() || urlSegments.pop(); // Handle potential trailing slash
      if (urlLast === "redirect") {
        // Get whatever we need from the rsoSession
        const { idToken, accessToken, gameId } = rsoSession;
        const rsoParsed = { idToken, accessToken, gameId };
        safeLocalStorage.setItem(siteConfig.KEY_RSO_SESSION, JSON.stringify(rsoParsed));
        return rsoParsed;
      } else {
        logger(
          "Unable to get the token. rso.getSession()",
          new Error("Unable to get the token. rso.getSession()"),
          "error",
          { ...rsoSession },
        );
      }
    }
    // Player is already logged-in
    return savedSession;
  }, [rsoSession, savedSession]);

  // Parse player name, and tag from idToken
  const { game_name, tag_line } = React.useMemo(() => {
    if (!session) {
      return {};
    }
    try {
      // Parse the token
      // https://stackoverflow.com/a/46188039/1428241
      const { acct: { game_name = "", tag_line = "" } = {} } = parseIdToken(session.idToken);

      // game_name can be null, no problemo! That is expeced and no need error handling here.
      return { game_name, tag_line };
    } catch (err) {
      logger("idToken parsing error while extracting the game_name", err as Error, "warn");
      return {};
    }
  }, [session]);

  // Parse country from idToken
  const country = React.useMemo(() => {
    if (!session) {
      return "";
    }
    try {
      const { country = "" } = parseIdToken(session.idToken);
      return country.toUpperCase();
    } catch (err) {
      logger("idToken parsing error while extracting the country", err as Error, "warn");
      return "";
    }
  }, [session]);

  // Set Auth State
  React.useEffect(() => {
    // This simply ensure we only set the token once rso is ready
    if (!rsoReady) {
      return;
    }

    setAuthState({
      rsoReady: rsoReady,
      user: {
        game_name,
        tag_line,
      },
      country: country,
      session: session,
      login: login,
      logout: logout,
      clearSession: clearSession,
      hookLoginFunctions: hookLoginFunctions,
    });
  }, [session, rsoReady, country]);

  if (typeof window === "undefined") {
    return <RSOContext.Provider value={EMPTY_AUTH_STATE}>{children}</RSOContext.Provider>;
  }

  return <RSOContext.Provider value={authState}>{children}</RSOContext.Provider>;
};
